import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { CalendarIcon } from 'lucide-react'
import { format } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { useAuditLogStatistics } from '@/hooks/useAuditLogStatistics'
import { Skeleton } from '@/components/ui/skeleton'

export function AuditLogStatistics() {
  const [startDate, setStartDate] = useState<Date>()
  const [endDate, setEndDate] = useState<Date>()

  const { statistics, isLoading, isError } = useAuditLogStatistics({
    start_date: startDate ? startDate.toISOString() : undefined,
    end_date: endDate ? endDate.toISOString() : undefined,
  })

  if (isError) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-destructive">載入統計資訊失敗。請稍後再試。</div>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Skeleton className="h-[200px]" />
        <Skeleton className="h-[200px]" />
        <Skeleton className="h-[200px]" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Date Range Filter */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-sm font-medium mb-2 block">開始日期</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, 'PPP', { locale: zhTW }) : '選擇日期'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={startDate} onSelect={setStartDate} />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">結束日期</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, 'PPP', { locale: zhTW }) : '選擇日期'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={endDate} onSelect={setEndDate} />
                </PopoverContent>
              </Popover>
            </div>

            {(startDate || endDate) && (
              <Button
                variant="ghost"
                onClick={() => {
                  setStartDate(undefined)
                  setEndDate(undefined)
                }}
              >
                清除
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {statistics && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {/* Total Count */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">總操作次數</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{statistics.total.toLocaleString()}</div>
            </CardContent>
          </Card>

          {/* By Action */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">操作類型分佈</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(statistics.by_action).map(([action, count]) => (
                  <div key={action} className="flex justify-between items-center">
                    <span className="text-sm">
                      {action === 'CREATE'
                        ? '新增'
                        : action === 'READ'
                          ? '查詢'
                          : action === 'UPDATE'
                            ? '更新'
                            : action === 'DELETE'
                              ? '刪除'
                              : action}
                    </span>
                    <span className="font-semibold">{count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* By Resource Type */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">資源類型分佈</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {Object.entries(statistics.by_resource_type)
                  .sort((a, b) => b[1] - a[1])
                  .map(([type, count]) => (
                    <div key={type} className="flex justify-between items-center">
                      <span className="text-sm">{type}</span>
                      <span className="font-semibold">{count.toLocaleString()}</span>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>

          {/* Top Users */}
          <Card className="md:col-span-2 lg:col-span-3">
            <CardHeader>
              <CardTitle className="text-sm font-medium">活躍使用者 Top 10</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 text-sm font-medium">排名</th>
                      <th className="text-left p-2 text-sm font-medium">使用者 ID</th>
                      <th className="text-left p-2 text-sm font-medium">使用者名稱</th>
                      <th className="text-right p-2 text-sm font-medium">操作次數</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statistics.by_user.map((user, index) => (
                      <tr key={user.user_id} className="border-b">
                        <td className="p-2 text-sm">#{index + 1}</td>
                        <td className="p-2 text-sm font-mono">{user.user_id}</td>
                        <td className="p-2 text-sm">{user.user_name || '-'}</td>
                        <td className="p-2 text-sm text-right font-semibold">
                          {user.count.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
